import _ from 'lodash'
import { ValidityDeterminate } from '@fusion-advantage/backcompat'
import { PrescriptionReviewConcept, cidForDirItemId, medReconItemMustConfirm, medReconItemIsReconciled, ConceptActivation, ExecutableCalc, ExecutableCalcCalculateFunction, ExecutableCalcCalculateOk, CalcRuntimeError, CalcType } from '@fusion-advantage/formlib'
import { ok, Result } from 'neverthrow'
import { FormRootState } from '../../redux/formStore'
import { createSelector } from '@reduxjs/toolkit'

//
//
// PRESCRIPTIONS
//

/* comment per ticket 1309
export const createPrescriptionGlobalCalcSelector = () => {
	return createSelector(
		(state: FormRootState) => state.drug.models,
		(models) => {
			return [
				// NOTE: do not add a else to this to undo the truthy condition
				// any change to prescriptions would trigger this calc to re-run and
				// override any manual selections
				new ValueCalc(
					"c8HdaH4dOx6R4OEP8bKGdVo8", // financial concerns types
					[PrescriptionReviewConcept.cid],
					(ctx) => {
						const tooExpensiveSelected = models.find(x => 
							x.InUse === false &&
							x.Reason === MedReconItemNotInUseReason.TooExpensive
						)
						
						// Per ticket 1211 - if too expensive, then select medications for financial concerns types
						if(tooExpensiveSelected) {
							return {
								value: true,
								subconceptCid: cidForDirValue("Medications")
							}
						}
					},
					{
						global: true
					},
				)
			]
		}
	)
}
*/
export const createNotTakingMedsValueCalc = (getState: () => FormRootState): ExecutableCalc => {
	const selectCalculateFn = createSelector(
		(state: FormRootState) => state.drug.models,
		(models): ExecutableCalcCalculateFunction => {
			return (ctx): Result<ExecutableCalcCalculateOk, CalcRuntimeError> => {
				// Null when disabled (has active prescriptions)
				if (models && models.length > 0 && !models.every(x => x.InUse === false)) {
					return ok({ calcType: CalcType.Value, returnValue: [{ value: null }], printed: [] })
				}

				// Null when "presents medications only" is selected
				const presentsMedication = ctx.statusValue(cidForDirItemId(1226))
				if (presentsMedication) {
					return ok({ calcType: CalcType.Value, returnValue: [{ value: null }], printed: [] })
				}

				return ok({ calcType: CalcType.Value, returnValue: [], printed: [] })
			}
		}
	)
	return new ExecutableCalc({
		calcType: CalcType.Value,
		outputCid: cidForDirItemId(80),
		inputCids: [PrescriptionReviewConcept.cid, cidForDirItemId(1226)],
		selectCalculate: () => selectCalculateFn(getState()),
	})
}

export const createPrescriptionActivationCalc = (getState: () => FormRootState): ExecutableCalc => {
	const selectCalculateFn = createSelector(
		(state: FormRootState) => state.drug.models,
		(models): ExecutableCalcCalculateFunction => {
			return (ctx): Result<ExecutableCalcCalculateOk, CalcRuntimeError> => {
				if(!models || models.length === 0) {
					return ok({
						calcType: CalcType.Activation,
						returnValue: ConceptActivation.Active,
					})
				}

				if(models.every(x => x.InUse === false)) {
					return ok({
						calcType: CalcType.Activation,
						returnValue: ConceptActivation.Active,
					})
				}

				return ok({
					calcType: CalcType.Activation,
					returnValue: ConceptActivation.Disabled,
				})
			}
		}
	)
	return new ExecutableCalc({
		calcType: CalcType.Activation,
		outputCid: cidForDirItemId(80),
		inputCids: [PrescriptionReviewConcept.cid],
		selectCalculate: () => selectCalculateFn(getState())
	})
}

export const createPrescriptionValidationCalc = (getState: () => FormRootState): ExecutableCalc => {
	const selectCalculateFn = createSelector(
		(state: FormRootState) => state.drug.models,
		(models): ExecutableCalcCalculateFunction => {
			return (ctx): Result<ExecutableCalcCalculateOk, CalcRuntimeError> => {
				const patientPresentsMedsOnly = ctx.statusValue(cidForDirItemId(1226))
				const patientNotTakingMeds = ctx.statusValue(cidForDirItemId(80))

				if(patientPresentsMedsOnly) {
					return ok({
						calcType: CalcType.Validation,
						returnValue: { determinate: ValidityDeterminate.Valid }
					})
				}

				const allModelsNotInUse = models?.every(x => x.InUse === false)

				if((models?.length === 0 || allModelsNotInUse) && !patientNotTakingMeds) {
					return ok({
						calcType: CalcType.Validation,
						returnValue: {
							determinate: ValidityDeterminate.Required,
							message: 'Add RX medications or select patient not taking medications',
						}
					})
				}

				if(models) {
					for(let model of models) {
						if(medReconItemMustConfirm(model)) {
							return ok({
								calcType: CalcType.Validation,
								returnValue: {
									determinate: ValidityDeterminate.Required,
									message: 'Prior drugs must have a confirmation'
								}
							})
						}

						if(model.Indications.length <= 0 && model.InUse === true) {
							return ok({
								calcType: CalcType.Validation,
								returnValue: {
									determinate: ValidityDeterminate.Required,
									message: 'Indication required for any in-use prescription',
								}
							})
						}

						if(model.InUse === false && model.Reason === null) {
							return ok({
								calcType: CalcType.Validation,
								returnValue: {
									determinate: ValidityDeterminate.Required,
									message: 'Reason is required for each prescription that is no longer in use',
								}
							})
						}

						if(medReconItemIsReconciled(model) === false) {
							return ok({
								calcType: CalcType.Validation,
								returnValue: {
									determinate: ValidityDeterminate.Required,
									message: 'All drugs must be reconciled',
								}
							})
						}
					}
				}
				return ok({
					calcType: CalcType.Validation,
					returnValue: { determinate: ValidityDeterminate.Valid }
				})
			}
		}
	)
	return new ExecutableCalc({
		calcType: CalcType.Validation,
		outputCid: PrescriptionReviewConcept.cid,
		inputCids: [PrescriptionReviewConcept.cid, cidForDirItemId(1226), cidForDirItemId(80)],
		selectCalculate: () => selectCalculateFn(getState())
	})
}
